import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { adminActivityLogApi, userApi, downloadAuthed } from '../api/services';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { Download, Loader2, Search, RotateCcw, Eye } from 'lucide-react';
import InfoTip from '../components/InfoTip';

const EVENT_TYPE_COLORS = {
  auth:           'bg-purple-100 text-purple-800',
  page_view:      'bg-gray-100 text-gray-700',
  data_read:      'bg-blue-100 text-blue-800',
  mutation:       'bg-green-100 text-green-800',
  file:           'bg-amber-100 text-amber-800',
  button_click:   'bg-sky-100 text-sky-800',
  error:          'bg-red-100 text-red-800',
};

const PAGE_SIZE = 100;

export default function ActivityLog() {
  const [filters, setFilters] = useState({
    user_id: '',
    event_type: '',
    event_name: '',
    entity_type: '',
    entity_id: '',
    from: '',
    to: '',
  });
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [detail, setDetail] = useState(null);

  // Load user options once for the filter dropdown
  useEffect(() => {
    userApi.list().then((res) => {
      setUsers(res.data.users || res.data || []);
    }).catch(() => { /* ignore */ });
  }, []);

  const load = useCallback(async (opts = {}) => {
    setLoading(true);
    try {
      const params = {};
      for (const [k, v] of Object.entries(filters)) if (v) params[k] = v;
      params.limit = PAGE_SIZE;
      params.offset = opts.offset ?? offset;
      const res = await adminActivityLogApi.list(params);
      setRows(res.data.rows || []);
      setTotal(res.data.total || 0);
      if (opts.offset != null) setOffset(opts.offset);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load activity');
    } finally { setLoading(false); }
  }, [filters, offset]);

  useEffect(() => { load({ offset: 0 }); /* eslint-disable-next-line */ }, []);

  function applyFilters() { load({ offset: 0 }); }
  function resetFilters() {
    setFilters({ user_id: '', event_type: '', event_name: '', entity_type: '', entity_id: '', from: '', to: '' });
    setTimeout(() => load({ offset: 0 }), 0);
  }
  function downloadCsv() {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(filters)) if (v) qs.set(k, v);
    // Use the authenticated download helper so the Bearer token travels
    // with the request (window.open would drop it → 401).
    const path = `/admin/activity-logs/export.csv${qs.toString() ? `?${qs.toString()}` : ''}`;
    downloadAuthed(path, 'activity-log.csv').catch(() => toast.error('Download failed'));
  }

  return (
    <div>

      <div className="p-6">
        <p className="text-sm text-gray-500 mb-4">
          Every action taken by every user, recorded automatically.{' '}
          <InfoTip title="What this log captures" align="right">
            Each row is one action: a login, a page opened, data viewed, a
            change made (<b>mutation</b>), a file uploaded or downloaded, a
            button clicked, or an error. The log is append-only — entries can
            never be edited or deleted, by anyone — so it's a reliable record
            of who did what and when. Use the filters to narrow to a person,
            an order, or a time window, and click the eye icon for full detail.
          </InfoTip>
        </p>
        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Select label="User" value={filters.user_id}
                    onChange={(v) => setFilters({ ...filters, user_id: v })}
                    options={[{ value: '', label: 'Any user' }].concat(
                      users.map((u) => ({ value: u.id, label: `${u.first_name || ''} ${u.last_name || ''} <${u.email}>`.trim() }))
                    )} />
            <Select label="Event type" value={filters.event_type}
                    onChange={(v) => setFilters({ ...filters, event_type: v })}
                    options={[
                      { value: '',             label: 'Any type' },
                      { value: 'auth',         label: 'Auth' },
                      { value: 'page_view',    label: 'Page view' },
                      { value: 'data_read',    label: 'Data read' },
                      { value: 'mutation',     label: 'Mutation' },
                      { value: 'file',         label: 'File' },
                      { value: 'button_click', label: 'Button click' },
                      { value: 'error',        label: 'Error' },
                    ]} />
            <Text label="Event name" value={filters.event_name}
                  onChange={(v) => setFilters({ ...filters, event_name: v })}
                  placeholder="e.g. order.submit_for_review" />
            <Text label="Entity type" value={filters.entity_type}
                  onChange={(v) => setFilters({ ...filters, entity_type: v })}
                  placeholder="e.g. order, batch" />
            <Text label="Entity ID" value={filters.entity_id}
                  onChange={(v) => setFilters({ ...filters, entity_id: v })} />
            <Text label="From" type="datetime-local" value={filters.from}
                  onChange={(v) => setFilters({ ...filters, from: v })} />
            <Text label="To" type="datetime-local" value={filters.to}
                  onChange={(v) => setFilters({ ...filters, to: v })} />
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={applyFilters}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
              <Search className="w-4 h-4" /> Apply
            </button>
            <button onClick={resetFilters}
                    className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50">
              <RotateCcw className="w-4 h-4" /> Reset
            </button>
            <button onClick={downloadCsv}
                    className="ml-auto flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50">
              <Download className="w-4 h-4" /> Export CSV
            </button>
          </div>
        </div>

        {/* Results */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-2.5 border-b border-gray-200 text-xs text-gray-500 flex items-center justify-between">
            <span>{total.toLocaleString()} total · showing {rows.length} from offset {offset}</span>
            <div className="flex gap-1">
              <button onClick={() => load({ offset: Math.max(0, offset - PAGE_SIZE) })}
                      disabled={offset === 0 || loading}
                      className="px-2 py-1 border border-gray-300 rounded text-xs disabled:opacity-50">Prev</button>
              <button onClick={() => load({ offset: offset + PAGE_SIZE })}
                      disabled={offset + PAGE_SIZE >= total || loading}
                      className="px-2 py-1 border border-gray-300 rounded text-xs disabled:opacity-50">Next</button>
            </div>
          </div>
          {loading ? (
            <div className="p-12 text-center text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-center text-gray-500 text-sm">No activity matches these filters.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="text-left px-3 py-2">When</th>
                  <th className="text-left px-3 py-2">User</th>
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-left px-3 py-2">Event</th>
                  <th className="text-left px-3 py-2">Entity</th>
                  <th className="text-left px-3 py-2">IP</th>
                  <th className="text-right px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-xs text-gray-600 whitespace-nowrap">
                      {r.created_at && format(new Date(r.created_at), 'dd MMM HH:mm:ss')}
                    </td>
                    <td className="px-3 py-2 text-xs">{r.user_email || '(system)'}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${EVENT_TYPE_COLORS[r.event_type] || 'bg-gray-100 text-gray-700'}`}>
                        {r.event_type}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{r.event_name}</td>
                    <td className="px-3 py-2 text-xs text-gray-600">
                      {r.entity_type && r.entity_id ? (
                        <span>{r.entity_type}:<span className="font-mono text-[11px]">{String(r.entity_id).slice(0, 8)}…</span></span>
                      ) : '—'}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-500 font-mono">{r.ip_address || '—'}</td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => setDetail(r)} className="text-blue-600 hover:underline text-xs">
                        <Eye className="w-3.5 h-3.5 inline" /> detail
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {detail && <DetailModal row={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function Text({ label, value, onChange, placeholder, type = 'text' }) {
  return (
    <label className="block">
      <span className="block text-xs text-gray-600 mb-1">{label}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
             className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm" />
    </label>
  );
}

function Select({ label, value, onChange, options }) {
  return (
    <label className="block">
      <span className="block text-xs text-gray-600 mb-1">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white">
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

function DetailModal({ row, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-auto shadow-xl"
           onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold">{row.event_name}</div>
            <div className="text-xs text-gray-500">#{row.id} · {format(new Date(row.created_at), 'dd MMM yyyy HH:mm:ss')}</div>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800">✕</button>
        </div>
        <div className="px-5 py-4 space-y-3 text-sm">
          <Field label="User">{row.user_email || '(system)'}</Field>
          <Field label="Type">{row.event_type}</Field>
          <Field label="Entity">{row.entity_type}:{row.entity_id || '—'}</Field>
          <Field label="Session">{row.session_id || '—'}</Field>
          <Field label="IP / UA">
            {row.ip_address || '—'}<br />
            <span className="text-xs text-gray-500">{row.user_agent || ''}</span>
          </Field>
          {row.metadata && (
            <Field label="Metadata">
              <pre className="bg-gray-50 rounded p-2 text-[11px] font-mono overflow-x-auto">
                {typeof row.metadata === 'string' ? row.metadata : JSON.stringify(row.metadata, null, 2)}
              </pre>
            </Field>
          )}
          {row.entity_type === 'order' && row.entity_id && (
            <Link to={`/orders/${row.entity_id}`} className="inline-block text-blue-600 hover:underline text-xs">
              Open order →
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-0.5">{label}</div>
      <div className="text-sm text-gray-800">{children}</div>
    </div>
  );
}
